import React, { useState, useEffect } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { useNavigate, Link } from 'react-router-dom';
// Import icons
import { FaUsers, FaUserTie, FaHome, FaTools, FaFileInvoiceDollar, FaCog, FaCalendarAlt, FaClipboardList, FaChartBar, FaWrench, FaChartLine, FaSignOutAlt, FaUserCircle, FaHistory } from 'react-icons/fa';
import { terminology } from '../config/terminology'; // Import terminology
import useDemoPermissions from '../hooks/useDemoPermissions';
import { useUser } from '../context/UserContext';
import WelcomeModal from '../components/WelcomeModal';
import { markDemoWelcomeSeen } from '../services/userService';

// Define module data with separate Job entries - using modern colors now
const modules = [
  { name: 'Franchisees', path: '/franchisees', icon: FaUserTie, color: 'bg-jims-cyan', hoverColor: 'hover:bg-jims-cyan-dark' },
  { name: terminology.client.plural, path: '/clients', icon: FaUsers, color: 'bg-jims-cyan-dark', hoverColor: 'hover:bg-jims-cyan' }, // Use terminology
  { name: terminology.household.plural, path: '/households', icon: FaHome, color: 'bg-jims-cyan', hoverColor: 'hover:bg-jims-cyan-dark' }, // Use terminology for Household
  // Separated Jobs into Scheduler and List
  { name: 'Job Scheduler', path: '/job-scheduler', icon: FaCalendarAlt, color: 'bg-jims-cyan-dark', hoverColor: 'hover:bg-jims-cyan' },
  { name: 'Jobs List', path: '/jobs-list', icon: FaClipboardList, color: 'bg-jims-cyan-dark', hoverColor: 'hover:bg-jims-cyan' },
  { name: 'Services', path: '/services', icon: FaTools, color: 'bg-jims-cyan', hoverColor: 'hover:bg-jims-cyan-dark' },
  { name: 'Invoices', path: '/invoices', icon: FaFileInvoiceDollar, color: 'bg-jims-cyan-dark', hoverColor: 'hover:bg-jims-cyan' },
  { name: 'Chart of Accounts', path: '/chart-of-accounts', icon: FaChartBar, color: 'bg-jims-cyan', hoverColor: 'hover:bg-jims-cyan-dark' },
  { name: 'Reports', path: '/reports', icon: FaChartLine, color: 'bg-jims-cyan-dark', hoverColor: 'hover:bg-jims-cyan' },
  { name: 'Utilities', path: '/utilities', icon: FaWrench, color: 'bg-jims-cyan-dark', hoverColor: 'hover:bg-jims-cyan', requiresAdmin: true },
  { name: 'Account', path: '/account', icon: FaUserCircle, color: 'bg-jims-cyan', hoverColor: 'hover:bg-jims-cyan-dark' },
  // { name: 'Territories', path: '/territories', icon: FaMapMarkedAlt, color: 'bg-jims-cyan', hoverColor: 'hover:bg-jims-cyan-dark' },
  { name: 'Activity Log', path: '/activity-log', icon: FaHistory, color: 'bg-jims-cyan', hoverColor: 'hover:bg-jims-cyan-dark' },
  { name: 'Settings', path: '/settings', icon: FaCog, color: 'bg-gray-500', hoverColor: 'hover:bg-gray-600', requiresAdmin: true }, // Keep settings gray
];

const Home = () => {
  const navigate = useNavigate();
  const { isDemo } = useDemoPermissions();
  const { userInfo } = useUser();

  // Works for demo users (userInfo.firstName), franchisees (userInfo.franchiseeData.firstName),
  // and falls back to the legacy single-name field or a generic greeting.
  const firstName =
    userInfo?.firstName ||
    userInfo?.franchiseeData?.firstName ||
    userInfo?.name?.split(' ')[0] ||
    '';

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login'); // Redirect to login page after logout
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const visibleModules = modules.filter((m) => !m.requiresAdmin || !isDemo);

  // Show the welcome modal once for new demo users who haven't seen it yet.
  const [showWelcome, setShowWelcome] = useState(false);
  useEffect(() => {
    if (isDemo && userInfo && !userInfo.hasSeenWelcome) {
      setShowWelcome(true);
    }
  }, [isDemo, userInfo]);

  const handleCloseWelcome = () => {
    setShowWelcome(false);
    // Fire-and-forget: mark seen in Firestore so it won't show on next login
    markDemoWelcomeSeen().catch((err) =>
      console.warn('[Home] Failed to mark welcome as seen:', err)
    );
  };

  return (
    <div className="min-h-screen flex flex-col font-sans bg-gray-50/50">
      {/* Top Navigation Bar */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo area */}
            <div className="flex items-center space-x-4">
              <img 
                src="/logo-light.png" 
                alt="Loope Logo" 
                className="h-10 w-auto"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <span className="text-xl font-bold text-gray-900 border-l border-gray-200 pl-4 hidden sm:block">
                Admin Dashboard
              </span>
            </div>
            
            {/* Action area */}
            <div>
              <button
                onClick={handleLogout}
                className="inline-flex items-center space-x-2 bg-white border border-gray-300 rounded-full px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:text-red-600 hover:border-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 transition-all duration-200"
              >
                <span>Logout</span>
                <FaSignOutAlt className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-grow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full">
        <div className="mb-10 text-center sm:text-left">
          <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight sm:text-4xl">
            Welcome{firstName ? `, ${firstName}` : ' back'}
          </h1>
          <p className="mt-3 text-lg text-gray-500 max-w-2xl">
            Select a module below to manage operations.
          </p>
        </div>

        {/* Module Links Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {visibleModules.map((module) => {
            return (
              <Link
                key={module.name}
                to={module.path}
                className="bg-white rounded-2xl shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 ring-1 ring-gray-900/5 p-6 flex flex-col items-center justify-center text-center group"
              >
                <div className="h-16 w-16 mb-4 rounded-full bg-green-50 group-hover:bg-green-100 flex items-center justify-center transition-colors">
                  <module.icon className="h-8 w-8 text-green-600 group-hover:text-green-700" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">{module.name}</h3>
              </Link>
            );
          })}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-8 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm text-gray-500">
            &copy; {new Date().getFullYear()} Loope Admin. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Welcome modal — shown once to new demo users */}
      <WelcomeModal
        isOpen={showWelcome}
        onClose={handleCloseWelcome}
        firstName={firstName}
      />
    </div>
  );
};

export default Home;
